import {
	Grid,
	Paper,
	SortDirection,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { visuallyHidden } from "@mui/utils"
import React from "react"
import { useSelector } from "react-redux"

import { useTranslation } from "react-i18next"
import { RootState } from "../../../store"
import { PlicksListState, PlickState } from "../../../store/actions/file-detail"
import PlickRow from "./row"

interface HeadCell {
	id: keyof PlickState
	label: string
	numeric: boolean
	disablePadding: boolean
}

interface IEnhancedPlickTable {
	sortCriteria: [{ field: string; order: string }]
	setSortCriteria: (value: [{ field: string; order: string }]) => void
}

interface IEnhancedTableHeadProps {
	sortCriteria: [{ field: string; order: string }]
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof PlickState
	) => void
}

const TableCellStyled = styled(TableCell)(() => ({
	paddingLeft: "8px",
}))

const TableHeadStyled = styled(TableHead)(() => ({
	backgroundColor: "#E9EDF0",
}))

function EnhancedTableHead(props: IEnhancedTableHeadProps) {
	const { sortCriteria, onRequestSort } = props
	const { t } = useTranslation()

	const createSortHandler =
		(property: keyof PlickState) => (event: React.MouseEvent<unknown>) => {
			onRequestSort(event, property)
		}

	const headCells: HeadCell[] = [
		{
			id: "transactionId",
			numeric: false,
			disablePadding: true,
			label: t("table.transactionId", { ns: "plick" }),
		},
		{
			id: "causal",
			numeric: false,
			disablePadding: false,
			label: t("table.causal", { ns: "plick" }),
		},
		{
			id: "notes",
			numeric: false,
			disablePadding: false,
			label: t("table.note", { ns: "plick" }),
		},
		{
			id: "activationDate",
			numeric: false,
			disablePadding: false,
			label: t("table.activationDate", { ns: "plick" }),
		},
		{
			id: "receiverName",
			numeric: false,
			disablePadding: false,
			label: t("table.receiver", { ns: "plick" }),
		},
		{
			id: "amount",
			numeric: true,
			disablePadding: false,
			label: t("table.amount", { ns: "plick" }),
		},
		{
			id: "status",
			numeric: false,
			disablePadding: false,
			label: t("table.status", { ns: "plick" }),
		},
	]
	return (
		<TableHeadStyled>
			<TableRow>
				<TableCellStyled />
				{headCells.map((headCell) => {
					const cellSorted:
						| { field: string; order: SortDirection }
						| undefined = sortCriteria.find(
						(criteria: { field: string; order: SortDirection }) =>
							criteria.field === headCell.id
					)
					return (
						<TableCellStyled
							key={headCell.id}
							align={headCell.numeric ? "right" : "left"}
							padding={headCell.disablePadding ? "none" : "normal"}
							sortDirection={!!cellSorted ? cellSorted.order : undefined}
						>
							<TableSortLabel
								active={!!cellSorted}
								direction={!!cellSorted ? cellSorted.order : "asc"}
								onClick={createSortHandler(headCell.id)}
							>
								{headCell.label}{" "}
								{!!cellSorted ? (
									<span style={visuallyHidden}>
										{" "}
										{cellSorted.order === "desc"
											? "sorted descending"
											: "sorted ascending"}{" "}
									</span>
								) : null}
							</TableSortLabel>
						</TableCellStyled>
					)
				})}
				<TableCellStyled />
			</TableRow>
		</TableHeadStyled>
	)
}
const EnhancedPlickTable = ({
	sortCriteria,
	setSortCriteria,
}: IEnhancedPlickTable) => {
	const { t } = useTranslation()
	const plicksState: PlicksListState = useSelector(
		(state: RootState) => state.plicks
	)

	const itemsShowned = useSelector(
		(state: RootState) =>
			(state.plicks?.list?.page?.number + 1) *
			state.plicks?.list?.page?.numberOfElements
	)

	const totalItems = useSelector(
		(state: RootState) => state.plicks?.list?.page?.totalElements
	)

	const isMore = useSelector(
		(state: RootState) => !!state.plicks?.list?.nextUrl
	)

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof PlickState
	) => {
		const cellSorted: { field: string; order: SortDirection } | undefined =
			sortCriteria.find(
				(criteria: { field: string; order: SortDirection }) =>
					criteria.field === property
			)

		const isAsc = !!cellSorted ? cellSorted.order : false
		setSortCriteria([{ field: property, order: isAsc ? "desc" : "asc" }])
	}
	return (
		<>
			<TableContainer component={Paper} sx={{ marginTop: "16px" }}>
				<Table
					sx={{ minWidth: 750 }}
					aria-labelledby="tableTitle"
					size="medium"
				>
					<EnhancedTableHead
						sortCriteria={sortCriteria}
						onRequestSort={handleRequestSort}
					/>
					<TableBody>
						{plicksState?.list?.page?.content.map((row: PlickState) => (
							<PlickRow key={`Plick_${row.id}`} row={row} />
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<Grid
				container
				justifyContent="space-between"
				style={{ backgroundColor: "#FFF", fontWeight: "600", padding: "16px" }}
			>
				<Grid item> </Grid>
				<Grid item>{isMore ? t("loadMore", { ns: "common" }) : ""}</Grid>
				<Grid item>
					{itemsShowned} {t("of", { ns: "common" })} {totalItems}
				</Grid>
			</Grid>
		</>
	)
}
export default EnhancedPlickTable
